import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, Supplier, SupplierOrder, SupplierRating } from "../api";
import { GlassPanel, KpiCard, Badge, Modal, toast, SkeletonTable, DataTable, confirm } from "../components";
import type { Column } from "../components";
import { MAX_SELECTION, addToSelection, isStaleRun, pickerBlocksSubmit, shouldRetireSource } from "./orderPickerLogic";

function StarInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((s) => (
        <span
          key={s}
          onClick={() => onChange(s)}
          style={{ cursor: "pointer", fontSize: 22, color: s <= value ? "#f59e0b" : "var(--text-tertiary)" }}
        >
          {s <= value ? "★" : "☆"}
        </span>
      ))}
    </div>
  );
}

function ratingBadge(score: number): { text: string; variant: "ok" | "warn" | "bad" | "info" | "" } {
  if (score >= 80) return { text: `${score}`, variant: "ok" };
  if (score >= 60) return { text: `${score}`, variant: "info" };
  if (score >= 40) return { text: `${score}`, variant: "warn" };
  if (score > 0) return { text: `${score}`, variant: "bad" };
  return { text: "N/A", variant: "" };
}

const ORDER_PAGE_SIZE = 50;
const MISSING_FIELD_LABELS: Record<string, string> = {
  tax_id: "MST",
  address: "địa chỉ",
  phone: "SĐT",
  bank_account: "STK",
  supplier_name: "tên NCC",
  invoice_no: "số hóa đơn",
};
const missingFieldLabel = (f: string) => MISSING_FIELD_LABELS[f] || f;

/** Checkbox 18px trong vùng bấm >=40px; hỗ trợ indeterminate cho "chọn trang". */
function PageCheckbox({ checked, indeterminate = false, onChange, label }: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label className="order-check">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

/** Picker đơn dùng chung cho Apply (scope=all) và Merge (scope=supplier).
 *  Chỉ giữ ID đã chọn; đổi trang/tìm kiếm không mất lựa chọn. */
function useOrderPicker(scope: "all" | "supplier") {
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState("");
  const [queryInput, setQueryInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const seq = useRef(0);
  const supplierRef = useRef("");

  const fetchPage = async (supplierId: string, q: string, nextOffset: number) => {
    const my = ++seq.current;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const r = await api.suppliers.orders(supplierId, {
        scope,
        q: q || undefined,
        limit: ORDER_PAGE_SIZE,
        offset: nextOffset,
      });
      if (my !== seq.current) return;
      setOrders(r.orders || []);
      setTotal(Number(r.total) || 0);
      setOffset(Number(r.offset) || 0);
    } catch (e: unknown) {
      if (my !== seq.current) return;
      setOrders([]);
      setTotal(0);
      setError((e as Error).message);
    } finally {
      if (my === seq.current) setLoading(false);
    }
  };

  const reset = () => {
    seq.current += 1;
    supplierRef.current = "";
    setOrders([]);
    setSelected(new Set());
    setTotal(0);
    setOffset(0);
    setQuery("");
    setQueryInput("");
    setLoading(false);
    setError("");
    setNotice("");
  };

  const open = (supplierId: string) => {
    reset();
    supplierRef.current = supplierId;
    void fetchPage(supplierId, "", 0);
  };

  const search = (e?: FormEvent) => {
    e?.preventDefault();
    if (!supplierRef.current) return;
    const q = queryInput.trim();
    setQuery(q);
    setOffset(0);
    void fetchPage(supplierRef.current, q, 0);
  };

  const goPage = (nextOffset: number) => {
    if (!supplierRef.current || nextOffset < 0) return;
    void fetchPage(supplierRef.current, query, nextOffset);
  };

  const toggle = (id: number) => {
    setNotice("");
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size >= MAX_SELECTION) {
        setNotice(`Chọn tối đa ${MAX_SELECTION} đơn mỗi lần.`);
        return prev;
      } else next.add(id);
      return next;
    });
  };

  const togglePage = (checked: boolean) => {
    setNotice("");
    setSelected((prev) => {
      const ids = orders.map((o) => o.id);
      if (!checked) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      const { next, capped } = addToSelection(prev, ids, MAX_SELECTION);
      if (capped) setNotice(`Chọn tối đa ${MAX_SELECTION} đơn mỗi lần.`);
      return next;
    });
  };

  const pageIds = orders.map((o) => o.id);
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allPageSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const somePageSelected = selectedOnPage > 0 && !allPageSelected;

  return {
    orders, selected, total, offset, query, queryInput, loading, error, notice,
    pageIds, allPageSelected, somePageSelected, selectedOnPage,
    setQueryInput, open, reset, search, goPage, toggle, togglePage,
  };
}

function goodsTypeLabel(gt?: string | null) {
  switch (gt) {
    case "vat": return "VAT";
    case "bangke": return "Bảng kê";
    case "vn": return "VN";
    case "tieungach": return "Tiểu ngạch";
    default: return gt || "—";
  }
}

type OrderPicker = ReturnType<typeof useOrderPicker>;

/** Bảng chọn đơn dùng chung: tìm kiếm chủ động (Enter), phân trang 50, chọn theo trang. */
function OrderPickerTable({ picker, emptyText }: { picker: OrderPicker; emptyText: string }) {
  const totalPages = Math.max(1, Math.ceil(picker.total / ORDER_PAGE_SIZE));
  const currentPage = Math.floor(picker.offset / ORDER_PAGE_SIZE) + 1;

  return (
    <div className="order-picker__body">
      <form className="order-picker__search" onSubmit={picker.search} role="search">
        <input
          type="search"
          value={picker.queryInput}
          onChange={(e) => picker.setQueryInput(e.target.value)}
          placeholder="Tìm mã đơn, NCC, số hóa đơn…"
          aria-label="Tìm đơn nhập"
        />
        <button type="submit" className="btn btn-ghost" disabled={picker.loading}>Tìm</button>
      </form>

      {picker.error ? (
        <div className="order-picker__state order-picker__state--error" role="alert">
          {picker.error}
          <button type="button" className="btn btn-ghost" onClick={() => picker.search()}>Thử lại</button>
        </div>
      ) : picker.loading ? (
        <SkeletonTable rows={4} cols={5} />
      ) : picker.orders.length === 0 ? (
        <div className="order-picker__state">{emptyText}</div>
      ) : (
        <div className="order-picker__table-wrap">
          <table className="data-table" style={{ width: "100%", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <PageCheckbox
                    checked={picker.allPageSelected}
                    indeterminate={picker.somePageSelected}
                    onChange={picker.togglePage}
                    label="Chọn tất cả đơn trên trang"
                  />
                </th>
                <th>Đơn</th>
                <th>Ngày</th>
                <th>Loại</th>
                <th className="order-picker__amount" style={{ textAlign: "right" }}>Tiền</th>
                <th className="order-picker__missing">Thiếu</th>
              </tr>
            </thead>
            <tbody>
              {picker.orders.map((o) => {
                const missing = o.missing_fields && o.missing_fields.length > 0;
                const label = `#${o.id}${o.supplier_name ? ` · ${o.supplier_name}` : ""}${o.invoice_no ? ` · HĐ ${o.invoice_no}` : ""}`;
                return (
                  <tr key={o.id}>
                    <td>
                      <PageCheckbox
                        checked={picker.selected.has(o.id)}
                        onChange={() => picker.toggle(o.id)}
                        label={label}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>#{o.id}</div>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                        {o.supplier_name || "—"}{o.invoice_no ? ` · HĐ ${o.invoice_no}` : ""}
                      </div>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{o.order_date ? new Date(o.order_date).toLocaleDateString("vi-VN") : "—"}</td>
                    <td>{goodsTypeLabel(o.goods_type)}</td>
                    <td className="order-picker__amount" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{o.total_amount.toLocaleString("vi-VN")}</td>
                    <td className="order-picker__missing">
                      {missing
                        ? <Badge variant="warn">{o.missing_fields!.map(missingFieldLabel).join(", ")}</Badge>
                        : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {picker.notice && <div className="order-picker__notice" role="status">{picker.notice}</div>}

      <div className="order-picker__footer">
        <div className="order-picker__pages">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={picker.loading || picker.offset === 0}
            onClick={() => picker.goPage(picker.offset - ORDER_PAGE_SIZE)}
          >
            ‹ Trước
          </button>
          <span>Trang {currentPage}/{totalPages} · {ORDER_PAGE_SIZE} đơn/trang · {picker.total} đơn</span>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={picker.loading || picker.offset + ORDER_PAGE_SIZE >= picker.total}
            onClick={() => picker.goPage(picker.offset + ORDER_PAGE_SIZE)}
          >
            Sau ›
          </button>
        </div>
        <span className="order-picker__count" aria-live="polite">Đã chọn {picker.selected.size} đơn</span>
      </div>
    </div>
  );
}

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  // Add/Edit modal
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formAddress, setFormAddress] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formTaxId, setFormTaxId] = useState("");
  const [formBankAccount, setFormBankAccount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // AI per-supplier
  const [aiProvider, setAiProvider] = useState("");
  const [aiModel, setAiModel] = useState("");
  // Rating modal
  const [showRating, setShowRating] = useState(false);
  const [ratingSupplier, setRatingSupplier] = useState<Supplier | null>(null);
  const [onTimeScore, setOnTimeScore] = useState(3);
  const [qualityScore, setQualityScore] = useState(3);
  const [priceScore, setPriceScore] = useState(3);
  const [ratingComment, setRatingComment] = useState("");
  // History modal
  const [showHistory, setShowHistory] = useState(false);
  const [historySupplier, setHistorySupplier] = useState<Supplier | null>(null);
  const [ratings, setRatings] = useState<SupplierRating[]>([]);
  const [loadingRatings, setLoadingRatings] = useState(false);

  // Apply-to-orders modal
  const [showApply, setShowApply] = useState(false);
  const [applySupplier, setApplySupplier] = useState<Supplier | null>(null);
  const [applySubmitting, setApplySubmitting] = useState(false);
  const applyRun = useRef(0);
  const applyPicker = useOrderPicker("all");

  // Merge-from-NCC modal
  const [showMerge, setShowMerge] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<Supplier | null>(null);
  const [mergeSourceId, setMergeSourceId] = useState<string>("");
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  const mergeRun = useRef(0);
  const mergePicker = useOrderPicker("supplier");

  useEffect(() => { loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const r = await api.suppliers.list("purchase");
      setSuppliers(r.suppliers || []);
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // KPIs
  const totalSuppliers = suppliers.length;
  const topSupplier = suppliers.length > 0 ? suppliers.reduce((a, b) => (a.rating_score || 0) > (b.rating_score || 0) ? a : b) : null;
  const weakSuppliers = suppliers.filter((s) => s.rating_score > 0 && s.rating_score < 60);

  // Form handlers
  const openAdd = () => {
    setEditId(null);
    setFormName(""); setFormPhone(""); setFormEmail(""); setFormAddress(""); setFormNotes("");
    setFormTaxId(""); setFormBankAccount("");
    setShowForm(true);
  };

  const openEdit = (s: Supplier) => {
    setEditId(s.id);
    setFormName(s.name); setFormPhone(s.phone || ""); setFormEmail(s.email || ""); setFormAddress(s.address || ""); setFormNotes(s.notes || "");
    setFormTaxId(s.tax_id || ""); setFormBankAccount(s.bank_account || "");
    setAiProvider(""); setAiModel("");
    setShowForm(true);
    // Load AI config for this supplier
    api.suppliers.getAi(s.id).then((r) => {
      setAiProvider(r.provider || "");
      setAiModel(r.model || "");
    }).catch(() => {});
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error("Tên NCC bắt buộc"); return; }
    setSubmitting(true);
    try {
      const data = { name: formName.trim(), phone: formPhone.trim() || undefined, email: formEmail.trim() || undefined, address: formAddress.trim() || undefined, notes: formNotes.trim() || undefined, tax_id: formTaxId.trim() || undefined, bank_account: formBankAccount.trim() || undefined };
      if (editId) {
        await api.suppliers.update(editId, data as any);
        // Save AI config
        await api.suppliers.setAi(editId, { provider: aiProvider, model: aiModel });
        toast.success("Cập nhật NCC thành công");
      } else {
        const res = await api.suppliers.create({ ...data, supplier_type: "purchase" });
        // Save AI config for new supplier
        if (res.id && (aiProvider || aiModel)) {
          await api.suppliers.setAi(res.id, { provider: aiProvider, model: aiModel });
        }
        toast.success("Thêm NCC thành công");
      }
      setShowForm(false);
      await loadSuppliers();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (s: Supplier) => {
    // Đếm SP đang ký gửi trước khi confirm
    let usageMsg = "";
    try {
      const u = await api.suppliers.usage(s.id);
      const n = Math.max(u.items_count, u.consignment_count);
      usageMsg = n > 0
        ? `NCC này đang có ${n} SP ký gửi. Xoá NCC sẽ gỡ link nhưng GIỮ NGUYÊN sản phẩm, giá, barcode và danh mục trên Loyverse. Tiếp tục?`
        : `NCC này chưa có SP nào. Tiếp tục xoá?`;
    } catch {
      usageMsg = `Xoá NCC sẽ gỡ link SP nhưng giữ nguyên sản phẩm, giá, danh mục trên Loyverse. Tiếp tục?`;
    }
    const ok = await confirm({ title: `Xoá NCC "${s.name}"`, message: usageMsg, danger: true, confirmLabel: "Xoá NCC" });
    if (!ok) return;
    try {
      const res = await api.suppliers.remove(s.id);
      toast.success(res.unlinked ? `Đã xoá NCC, gỡ link ${res.unlinked} SP` : "Đã xoá NCC");
      await loadSuppliers();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  // Chuyển NCC này sang loại ký gửi → biến mất khỏi trang này, hiện ở trang Hàng ký gửi.
  const handleConvert = async (s: Supplier) => {
    if (!await confirm({ title: "Chuyển sang ký gửi", message: `Chuyển "${s.name}" sang NCC ký gửi? NCC sẽ chuyển sang quản lý ở trang Hàng ký gửi.`, confirmLabel: "Chuyển" })) return;
    try {
      await api.suppliers.setType(s.id, "consignment");
      toast.success("Đã chuyển sang ký gửi");
      await loadSuppliers();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  // Rating
  const openRating = (s: Supplier) => {
    setRatingSupplier(s);
    setOnTimeScore(3); setQualityScore(3); setPriceScore(3); setRatingComment("");
    setShowRating(true);
  };

  const handleSaveRating = async () => {
    if (!ratingSupplier) return;
    setSubmitting(true);
    try {
      await api.suppliers.ratings.create(ratingSupplier.id, {
        on_time_score: onTimeScore,
        quality_score: qualityScore,
        price_score: priceScore,
        comment: ratingComment.trim() || undefined,
      });
      toast.success("Đánh giá thành công");
      setShowRating(false);
      await loadSuppliers();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  // History
  const openHistory = async (s: Supplier) => {
    setHistorySupplier(s);
    setShowHistory(true);
    setLoadingRatings(true);
    try {
      const r = await api.suppliers.ratings.list(s.id);
      setRatings(r.ratings || []);
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      setLoadingRatings(false);
    }
  };

  const handleDeleteRating = async (ratingId: string) => {
    if (!historySupplier) return;
    try {
      await api.suppliers.ratings.remove(historySupplier.id, ratingId);
      setRatings((prev) => prev.filter((r) => r.id !== ratingId));
      toast.success("Đã xoá đánh giá");
      await loadSuppliers();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  // Áp dụng thông tin NCC vào các đơn nhập đã có (ghi đè full: tên/SĐT/địa chỉ/MST/STK).
  // Áp dụng thông tin NCC vào các đơn nhập đã có (ghi đè full: tên/SĐT/địa chỉ/MST/STK).
  const openApply = (s: Supplier) => {
    applyRun.current += 1;
    setApplySubmitting(false);
    setApplySupplier(s);
    setShowApply(true);
    applyPicker.open(s.id);
  };

  const closeApply = () => {
    setShowApply(false);
    applyPicker.reset();
  };

  const handleApply = async () => {
    if (!applySupplier) return;
    const ids = Array.from(applyPicker.selected);
    if (ids.length === 0) { toast.error("Chọn ít nhất 1 đơn"); return; }
    if (applySubmitting || applyPicker.loading) return;
    if (applyPicker.error) { toast.error("Danh sách đơn đang lỗi, không thể áp dụng"); return; }
    const gt = applySupplier.tax_id ? `MST: ${applySupplier.tax_id}\n` : "";
    const ok = await confirm({
      title: "Áp dụng NCC vào đơn",
      message: `Ghi đè THÔNG TIN NCC "${applySupplier.name}" lên ${ids.length} đơn nhập đã chọn?\n\nThông tin sẽ ghi đè (kể cả đơn đã nhập kho):\n• Tên NCC, SĐT, địa chỉ\n${gt}• Số TK ngân hàng\n\nKhông thể hoàn tác.`,
      danger: true,
      confirmLabel: "Áp dụng",
    });
    if (!ok) return;
    const run = ++applyRun.current;
    setApplySubmitting(true);
    try {
      const r = await api.suppliers.applyToOrders(applySupplier.id, { order_ids: ids });
      toast.success(`Đã áp dụng NCC vào ${r.updated} đơn`);
      if (!isStaleRun(run, applyRun.current)) closeApply();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      if (!isStaleRun(run, applyRun.current)) setApplySubmitting(false);
    }
  };

  // Gộp từ NCC khác: chọn NCC nguồn B → chọn đơn → chuyển sang NCC A.
  const openMerge = (s: Supplier) => {
    mergeRun.current += 1;
    setMergeSubmitting(false);
    setMergeTarget(s);
    setMergeSourceId("");
    setShowMerge(true);
    mergePicker.reset();
  };

  const closeMerge = () => {
    setShowMerge(false);
    mergePicker.reset();
  };

  const loadMergeOrders = (sourceId: string) => {
    setMergeSourceId(sourceId);
    if (!sourceId) { mergePicker.reset(); return; }
    mergePicker.open(sourceId);
  };

  const handleMerge = async () => {
    if (!mergeTarget || !mergeSourceId) { toast.error("Chọn NCC nguồn"); return; }
    const ids = Array.from(mergePicker.selected);
    if (ids.length === 0) { toast.error("Chọn ít nhất 1 đơn"); return; }
    if (mergeSubmitting || mergePicker.loading) return;
    if (mergePicker.error) { toast.error("Danh sách đơn đang lỗi, không thể gộp"); return; }
    const src = suppliers.find((s) => s.id === mergeSourceId);
    // Chỉ xoá NCC nguồn khi đã dời TOÀN BỘ đơn của NCC đó và không lọc.
    const retireSource = shouldRetireSource({ query: mergePicker.query, selectedCount: ids.length, total: mergePicker.total });
    const retireNote = retireSource
      ? `• NCC nguồn "${src?.name || ""}" sẽ bị XOÁ (sau khi dời hết đơn).`
      : `• NCC nguồn "${src?.name || ""}" KHÔNG bị xoá vì chưa dời hết đơn.`;
    const ok = await confirm({
      title: "Gộp đơn từ NCC khác",
      message: `Chuyển ${ids.length} đơn từ NCC "${src?.name || mergeSourceId}" sang NCC "${mergeTarget.name}"?\n\n• Thông tin NCC ${mergeTarget.name} sẽ ghi đè lên các đơn.\n${retireNote}\n\nKhông thể hoàn tác.`,
      danger: true,
      confirmLabel: "Gộp",
    });
    if (!ok) return;
    const run = ++mergeRun.current;
    setMergeSubmitting(true);
    try {
      const r = await api.suppliers.applyToOrders(mergeTarget.id, {
        order_ids: ids,
        ...(retireSource ? { from_supplier_ids_to_retire: [mergeSourceId] } : {}),
      });
      toast.success(`Đã gộp ${r.updated} đơn vào "${mergeTarget.name}"`);
      if (!isStaleRun(run, mergeRun.current)) closeMerge();
      await loadSuppliers();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    } finally {
      if (!isStaleRun(run, mergeRun.current)) setMergeSubmitting(false);
    }
  };

  const columns: Column<Supplier>[] = useMemo(() => [
    {
      key: "name",
      header: "Nhà cung cấp",
      render: (s) => (
        <div>
          <div style={{ fontWeight: 600 }}>{s.name}</div>
          {s.phone && <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{s.phone}</div>}
        </div>
      ),
    },
    {
      key: "rating_score",
      header: "Điểm",
      align: "center",
      sortable: true,
      render: (s) => {
        const score = s.rating_score || 0;
        const b = ratingBadge(score);
        return <Badge variant={b.variant}>{b.text}</Badge>;
      },
    },
    {
      key: "rating_count",
      header: "Lượt ĐG",
      align: "center",
      render: (s) => <span>{s.rating_count || 0}</span>,
    },
    {
      key: "avg_on_time",
      header: "Đúng hạn",
      align: "center",
      render: (s) => s.avg_on_time ? `${(s.avg_on_time).toFixed(1)}/5` : "—",
    },
    {
      key: "avg_quality",
      header: "Chất lượng",
      align: "center",
      render: (s) => s.avg_quality ? `${(s.avg_quality).toFixed(1)}/5` : "—",
    },
    {
      key: "avg_price",
      header: "Giá",
      align: "center",
      render: (s) => s.avg_price ? `${(s.avg_price).toFixed(1)}/5` : "—",
    },
    {
      key: "_actions",
      header: "",
      render: (s) => (
        <div style={{ display: "flex", gap: 4 }}>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px" }} onClick={(e) => { e.stopPropagation(); openRating(s); }}>
            Đánh giá
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px" }} onClick={(e) => { e.stopPropagation(); openHistory(s); }}>
            Lịch sử
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px", color: "var(--accent, #7c5cff)" }} onClick={(e) => { e.stopPropagation(); openApply(s); }} title="Ghi đè thông tin NCC này lên các đơn nhập đã có">
            Áp dụng vào đơn…
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px", color: "#7c9eff" }} onClick={(e) => { e.stopPropagation(); openMerge(s); }} title="Dời đơn từ NCC khác sang NCC này, xoá NCC nguồn">
            Gộp từ NCC khác
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px" }} onClick={(e) => { e.stopPropagation(); openEdit(s); }}>
            Sửa
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px", color: "#a78bfa" }} onClick={(e) => { e.stopPropagation(); handleConvert(s); }} title="Chuyển NCC này sang loại ký gửi">
            → Ký gửi
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 8px", color: "var(--accent-red)" }} onClick={(e) => { e.stopPropagation(); handleDelete(s); }} title="Xóa nhà cung cấp này">
            Xoá
          </button>
        </div>
      ),
    },
  ], []);

  return (
    <>
      <div className="kpi-grid">
        <KpiCard label="Tổng NCC" value={totalSuppliers.toString()} dotColor="blue" note="nhà cung cấp" />
        <KpiCard label="Top NCC" value={topSupplier?.name || "—"} dotColor="ok" note={topSupplier ? `${topSupplier.rating_score || 0} điểm` : ""} />
        <KpiCard label="NCC kém (<60)" value={weakSuppliers.length.toString()} dotColor="warn" note="cần cải thiện" />
      </div>

      <GlassPanel title="Nhà cung cấp" badge={`${totalSuppliers} NCC`}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, justifyContent: "flex-end" }}>
          <button className="btn btn-primary" onClick={openAdd}>+ Thêm NCC</button>
        </div>

        {loading ? <SkeletonTable rows={5} cols={7} /> : (
          <DataTable columns={columns} rows={suppliers} rowKey={(s) => s.id} pageSize={50} />
        )}
      </GlassPanel>

      {/* Modal Thêm/Sửa NCC */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editId ? "Sửa nhà cung cấp" : "Thêm nhà cung cấp"}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Tên NCC *</span>
            <input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Tên nhà cung cấp" />
          </label>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Điện thoại</span>
            <input value={formPhone} onChange={(e) => setFormPhone(e.target.value)} placeholder="SĐT" />
          </label>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Email</span>
            <input value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="Email" />
          </label>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Địa chỉ</span>
            <input value={formAddress} onChange={(e) => setFormAddress(e.target.value)} placeholder="Địa chỉ" />
          </label>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>MST / CCCD <span style={{ color: "var(--text-tertiary)", fontWeight: 400 }}>(Bảng kê 01/TNDN)</span></span>
            <input value={formTaxId} onChange={(e) => setFormTaxId(e.target.value)} placeholder="Mã số thuế hoặc CCCD" />
          </label>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Số tài khoản ngân hàng</span>
            <input value={formBankAccount} onChange={(e) => setFormBankAccount(e.target.value)} placeholder="STK + tên ngân hàng" />
          </label>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Ghi chú</span>
            <textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="Ghi chú" rows={2} />
          </label>
          <details className="ai-default-section" style={{ marginTop: 8, padding: 12, background: "rgba(255,255,255,0.02)", borderRadius: 8, border: "1px solid var(--border-subtle)" }}>
            <summary style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}>
              <span className="material-symbols-outlined" style={{ color: "var(--accent-ai, #a78bfa)", fontSize: 18 }}>auto_awesome</span>
              AI mặc định cho NCC này
            </summary>
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              <label>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Provider</span>
                <select value={aiProvider} onChange={(e) => setAiProvider(e.target.value)}>
                  <option value="">— Dùng global —</option>
                  <option value="gemini">Google Gemini</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="nvidia_nim">NVIDIA NIM</option>
                  <option value="ollama">Ollama Cloud</option>
                </select>
              </label>
              <label>
                <span style={{ fontSize: 12, fontWeight: 600 }}>Model</span>
                <input value={aiModel} onChange={(e) => setAiModel(e.target.value)} placeholder="vd: gemini-2.0-flash, gemma4:31b" />
              </label>
            </div>
          </details>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Huỷ</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={submitting}>
              {submitting ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Đánh giá */}
      <Modal open={showRating} onClose={() => setShowRating(false)} title={`Đánh giá: ${ratingSupplier?.name || ""}`}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Giao hàng đúng hạn</div>
            <StarInput value={onTimeScore} onChange={setOnTimeScore} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Chất lượng hàng hoá</div>
            <StarInput value={qualityScore} onChange={setQualityScore} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Giá cả hợp lý</div>
            <StarInput value={priceScore} onChange={setPriceScore} />
          </div>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Nhận xét</span>
            <textarea value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} placeholder="Nhận xét thêm..." rows={2} />
          </label>
          <div style={{ background: "var(--glass-bg)", borderRadius: 8, padding: 12, textAlign: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Điểm tổng: </span>
            <strong style={{ fontSize: 18 }}>{Math.round((onTimeScore + qualityScore + priceScore) / 3 * 20)}</strong>
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>/100</span>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={() => setShowRating(false)}>Huỷ</button>
            <button className="btn btn-primary" onClick={handleSaveRating} disabled={submitting}>
              {submitting ? "Đang lưu..." : "Lưu đánh giá"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Lịch sử đánh giá */}
      <Modal open={showHistory} onClose={() => setShowHistory(false)} title={`Lịch sử đánh giá: ${historySupplier?.name || ""}`}>
        {loadingRatings ? (
          <SkeletonTable rows={3} cols={5} />
        ) : ratings.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--text-secondary)" }}>Chưa có đánh giá nào</div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <table className="data-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Ngày</th>
                  <th style={{ textAlign: "center" }}>Đúng hạn</th>
                  <th style={{ textAlign: "center" }}>Chất lượng</th>
                  <th style={{ textAlign: "center" }}>Giá</th>
                  <th>Nhận xét</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {ratings.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontSize: 12 }}>{r.created_at ? new Date(r.created_at).toLocaleDateString("vi-VN") : "—"}</td>
                    <td style={{ textAlign: "center" }}>{r.on_time_score}/5</td>
                    <td style={{ textAlign: "center" }}>{r.quality_score}/5</td>
                    <td style={{ textAlign: "center" }}>{r.price_score}/5</td>
                    <td style={{ fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.comment || "—"}</td>
                    <td>
                      <button className="btn btn-ghost" style={{ fontSize: 10, padding: "2px 6px", color: "var(--accent-red)" }} onClick={() => handleDeleteRating(r.id)}>Xoá</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Modal Áp dụng NCC vào đơn */}
      <Modal open={showApply} onClose={closeApply} closeDisabled={applySubmitting} title={`Áp dụng "${applySupplier?.name || ""}" vào đơn`}>
        <div className="order-picker">
          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            Thông tin NCC (tên/SĐT/địa chỉ/MST/STK) sẽ ghi đè lên các đơn đã chọn — kể cả đơn đã nhập kho.
          </div>
          <OrderPickerTable picker={applyPicker} emptyText="Không có đơn nhập nào phù hợp." />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={closeApply} disabled={applySubmitting}>Huỷ</button>
            <button
              className="btn btn-primary"
              onClick={handleApply}
              disabled={pickerBlocksSubmit({
                submitting: applySubmitting, loading: applyPicker.loading,
                error: applyPicker.error, selectedCount: applyPicker.selected.size,
              })}
            >
              {applySubmitting ? "Đang áp dụng…" : `Áp dụng (${applyPicker.selected.size})`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Gộp từ NCC khác */}
      <Modal open={showMerge} onClose={closeMerge} closeDisabled={mergeSubmitting} title={`Gộp đơn vào "${mergeTarget?.name || ""}"`}>
        <div className="order-picker">
          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            Chọn NCC nguồn (B). Các đơn đã chọn sẽ được dời sang NCC này (ghi đè thông tin).
            NCC nguồn chỉ bị XOÁ khi toàn bộ đơn của NCC đó được dời.
          </div>
          <label>
            <span style={{ fontSize: 12, fontWeight: 600 }}>NCC nguồn</span>
            <select value={mergeSourceId} onChange={(e) => loadMergeOrders(e.target.value)}>
              <option value="">— Chọn NCC nguồn —</option>
              {suppliers.filter((s) => s.id !== mergeTarget?.id).map((s) => (
                <option key={s.id} value={s.id}>{s.name}{s.tax_id ? ` · MST ${s.tax_id}` : ""}</option>
              ))}
            </select>
          </label>
          {mergeSourceId ? (
            <OrderPickerTable picker={mergePicker} emptyText="NCC nguồn không có đơn nhập nào phù hợp." />
          ) : (
            <div style={{ textAlign: "center", padding: 24, color: "var(--text-secondary)" }}>Chọn NCC nguồn để xem đơn.</div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost" onClick={closeMerge} disabled={mergeSubmitting}>Huỷ</button>
            <button
              className="btn btn-primary"
              onClick={handleMerge}
              disabled={!mergeSourceId || pickerBlocksSubmit({
                submitting: mergeSubmitting, loading: mergePicker.loading,
                error: mergePicker.error, selectedCount: mergePicker.selected.size,
              })}
            >
              {mergeSubmitting ? "Đang gộp…" : `Gộp (${mergePicker.selected.size})`}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
